import { useCallback, useState } from "react";
import Page from "src/layouts/Page";

type Period = "7d" | "30d" | "90d";

const PERIODS: { key: Period; label: string }[] = [
	{ key: "7d", label: "7 days" },
	{ key: "30d", label: "30 days" },
	{ key: "90d", label: "90 days" }
];

type ContentTab = "Carousel" | "Promotions" | "Key Shouts" | "Quick Links";

const CONTENT_TABS: ContentTab[] = [
	"Carousel",
	"Promotions",
	"Key Shouts",
	"Quick Links"
];

const MOCK_CONTENT_ROWS: {
	name: string;
	impressions: number;
	clicks: number;
	ctr: string;
}[] = [
	{ name: "Spring Sale", impressions: 2340, clicks: 187, ctr: "8%" },
	{ name: "New Arrivals", impressions: 1890, clicks: 124, ctr: "6.6%" },
	{ name: "Team Brief", impressions: 1420, clicks: 73, ctr: "5.1%" }
];

const MOCK_DRAWER_ITEMS: { label: string; views: number }[] = [
	{ label: "Quick Links", views: 890 },
	{ label: "Form Submissions", views: 567 },
	{ label: "Events", views: 410 },
	{ label: "Key Shouts", views: 298 },
	{ label: "Social Feed", views: 210 }
];

const MOCK_BOTTOM_MENU: { label: string; views: number }[] = [
	{ label: "Home", views: 4500 },
	{ label: "Social", views: 3200 },
	{ label: "Search", views: 1870 },
	{ label: "Profile", views: 1230 }
];

const MOCK_ADOPTION_ROWS: { location: string; activeUsers: number }[] = [
	{ location: "North", activeUsers: 312 },
	{ location: "South", activeUsers: 245 },
	{ location: "East", activeUsers: 198 },
	{ location: "West", activeUsers: 176 }
];

const MOCK_HASHTAGS: { tag: string; count: number }[] = [
	{ tag: "teamwork", count: 320 },
	{ tag: "wins", count: 210 },
	{ tag: "retail", count: 180 }
];

const Analytics = () => {
	const [period, setPeriod] = useState<Period>("30d");
	const [contentTab, setContentTab] = useState<ContentTab>("Carousel");
	const [adoptionGroup, setAdoptionGroup] = useState<
		"Region" | "Area" | "Store"
	>("Region");

	const iosPct = Math.round((780 / (780 + 454)) * 100);
	const androidPct = 100 - iosPct;

	const handleTabKeyDown = useCallback(
		(
			e: React.KeyboardEvent<HTMLDivElement>,
			items: readonly string[],
			activeItem: string,
			setActive: (item: string) => void
		) => {
			const currentIndex = items.indexOf(activeItem);
			let nextIndex: number | null = null;

			if (e.key === "ArrowRight") {
				nextIndex = (currentIndex + 1) % items.length;
			} else if (e.key === "ArrowLeft") {
				nextIndex = (currentIndex - 1 + items.length) % items.length;
			} else if (e.key === "Home") {
				nextIndex = 0;
			} else if (e.key === "End") {
				nextIndex = items.length - 1;
			}

			if (nextIndex !== null) {
				e.preventDefault();
				setActive(items[nextIndex] as string);
				const tablist = e.currentTarget;
				const buttons =
					tablist.querySelectorAll<HTMLButtonElement>('[role="tab"]');
				buttons[nextIndex]?.focus();
			}
		},
		[]
	);

	return (
		<Page>
			{/* Period selector */}
			<div
				className="inline-flex bg-white border border-border p-1.5 rounded-xl shadow-xs mb-8"
				role="tablist"
				aria-label="Time period"
				onKeyDown={(e) =>
					handleTabKeyDown(
						e,
						PERIODS.map((p) => p.key),
						period,
						(v) => setPeriod(v as Period)
					)
				}
			>
				{PERIODS.map(({ key, label }) => (
					<button
						key={key}
						id={`period-tab-${key}`}
						type="button"
						role="tab"
						aria-selected={period === key}
						aria-controls="period-tabpanel"
						tabIndex={period === key ? 0 : -1}
						onClick={() => setPeriod(key)}
						className={[
							"px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200",
							period === key
								? "bg-primary text-white shadow-md shadow-primary/50"
								: "text-zinc-500 hover:text-foreground hover:bg-white/80"
						].join(" ")}
					>
						{label}
					</button>
				))}
			</div>

			<div
				id="period-tabpanel"
				role="tabpanel"
				aria-labelledby={`period-tab-${period}`}
			>
				{/* Section 1: Overview */}
				<div className="mb-8">
					<h2 className="font-heading text-lg font-medium text-foreground block mb-4">
						Overview
					</h2>
					<div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
						{/* Active Users */}
						<div className="rounded-xl border border-border bg-subtle shadow-sm p-6">
							<p className="text-sm text-muted-foreground mb-1">Active Users</p>
							<p className="text-2xl font-heading font-semibold text-foreground">
								1,234
							</p>
						</div>

						{/* Total Sessions */}
						<div className="rounded-xl border border-border bg-subtle shadow-sm p-6">
							<p className="text-sm text-muted-foreground mb-1">
								Total Sessions
							</p>
							<p className="text-2xl font-heading font-semibold text-foreground">
								5,678
							</p>
						</div>

						{/* Total Events */}
						<div className="rounded-xl border border-border bg-subtle shadow-sm p-6">
							<p className="text-sm text-muted-foreground mb-1">Total Events</p>
							<p className="text-2xl font-heading font-semibold text-foreground">
								98,765
							</p>
						</div>

						{/* Platform Split */}
						<div className="rounded-xl border border-border bg-subtle shadow-sm p-6">
							<p className="text-sm text-muted-foreground mb-1">
								Platform Split
							</p>
							<p className="text-2xl font-heading font-semibold text-foreground mb-3">
								iOS: 780 / Android: 454
							</p>
							<div className="flex h-2 rounded-full overflow-hidden">
								<div
									className="bg-primary h-full"
									style={{ width: `${iosPct}%` }}
								/>
								<div
									className="bg-green-500 h-full"
									style={{ width: `${androidPct}%` }}
								/>
							</div>
							<div className="flex justify-between mt-1.5">
								<span className="text-xs text-muted-foreground">
									iOS {iosPct}%
								</span>
								<span className="text-xs text-muted-foreground">
									Android {androidPct}%
								</span>
							</div>
						</div>
					</div>
				</div>

				{/* Section 2: Daily Active Users */}
				<div className="mb-8">
					<h2 className="font-heading text-lg font-medium text-foreground block mb-4">
						Daily Active Users
					</h2>
					<div className="rounded-xl border border-border bg-subtle shadow-sm p-6 flex items-center justify-center min-h-[200px]">
						<p className="text-muted-foreground text-sm">
							Chart will appear here when data is available
						</p>
					</div>
				</div>
			</div>

			{/* Section 3: Content Performance */}
			<div className="mb-8">
				<h2 className="font-heading text-lg font-medium text-foreground block mb-4">
					Content Performance
				</h2>
				<div className="rounded-xl border border-border bg-subtle shadow-sm p-6">
					{/* Tab buttons */}
					<div
						className="inline-flex bg-white border border-border p-1.5 rounded-xl shadow-xs mb-5"
						role="tablist"
						aria-label="Content type"
						onKeyDown={(e) =>
							handleTabKeyDown(e, CONTENT_TABS, contentTab, (v) =>
								setContentTab(v as ContentTab)
							)
						}
					>
						{CONTENT_TABS.map((tab) => (
							<button
								key={tab}
								id={`content-tab-${tab.toLowerCase().replace(/\s+/g, "-")}`}
								type="button"
								role="tab"
								aria-selected={contentTab === tab}
								aria-controls="content-tabpanel"
								tabIndex={contentTab === tab ? 0 : -1}
								onClick={() => setContentTab(tab)}
								className={[
									"px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200",
									contentTab === tab
										? "bg-primary text-white shadow-md shadow-primary/50"
										: "text-zinc-500 hover:text-foreground hover:bg-white/80"
								].join(" ")}
							>
								{tab}
							</button>
						))}
					</div>

					{/* Table */}
					<div
						id="content-tabpanel"
						role="tabpanel"
						aria-labelledby={`content-tab-${contentTab.toLowerCase().replace(/\s+/g, "-")}`}
					>
						<table className="w-full text-sm">
							<thead>
								<tr className="border-b border-border">
									<th className="text-left py-2 pr-4 font-medium text-muted-foreground">
										Name
									</th>
									<th className="text-right py-2 pr-4 font-medium text-muted-foreground">
										Impressions
									</th>
									<th className="text-right py-2 pr-4 font-medium text-muted-foreground">
										Clicks
									</th>
									<th className="text-right py-2 font-medium text-muted-foreground">
										CTR
									</th>
								</tr>
							</thead>
							<tbody>
								{MOCK_CONTENT_ROWS.map((row) => (
									<tr
										key={row.name}
										className="border-b border-border last:border-0"
									>
										<td className="py-3 pr-4 text-foreground">{row.name}</td>
										<td className="py-3 pr-4 text-right text-foreground">
											{row.impressions.toLocaleString()}
										</td>
										<td className="py-3 pr-4 text-right text-foreground">
											{row.clicks}
										</td>
										<td className="py-3 text-right text-foreground">
											{row.ctr}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			{/* Section 4: Social Engagement */}
			<div className="mb-8">
				<h2 className="font-heading text-lg font-medium text-foreground block mb-4">
					Social Engagement
				</h2>
				<div className="rounded-xl border border-border bg-subtle shadow-sm p-6">
					<div className="grid grid-cols-3 gap-4 mb-6">
						<div>
							<p className="text-sm text-muted-foreground mb-1">Posts Seen</p>
							<p className="text-2xl font-heading font-semibold text-foreground">
								45,000
							</p>
						</div>
						<div>
							<p className="text-sm text-muted-foreground mb-1">Reactions</p>
							<p className="text-2xl font-heading font-semibold text-foreground">
								1,800
							</p>
						</div>
						<div>
							<p className="text-sm text-muted-foreground mb-1">
								Videos Played
							</p>
							<p className="text-2xl font-heading font-semibold text-foreground">
								920
							</p>
						</div>
					</div>

					<div>
						<p className="text-sm font-medium text-muted-foreground mb-3">
							Top Hashtags
						</p>
						<ul className="space-y-2">
							{MOCK_HASHTAGS.map(({ tag, count }) => (
								<li
									key={tag}
									className="flex justify-between text-sm text-foreground"
								>
									<span className="text-zinc-600">#{tag}</span>
									<span className="font-medium">{count}</span>
								</li>
							))}
						</ul>
					</div>
				</div>
			</div>

			{/* Section 5: Navigation Usage */}
			<div className="mb-8">
				<h2 className="font-heading text-lg font-medium text-foreground block mb-4">
					Navigation Usage
				</h2>
				<div className="rounded-xl border border-border bg-subtle shadow-sm p-6 grid grid-cols-1 md:grid-cols-2 gap-8">
					{/* Drawer Items */}
					<div>
						<p className="text-sm font-medium text-muted-foreground mb-3">
							Drawer Items
						</p>
						<ol className="space-y-2">
							{MOCK_DRAWER_ITEMS.map(({ label, views }, i) => (
								<li key={label} className="flex items-center gap-3 text-sm">
									<span className="w-5 text-zinc-400 text-right shrink-0">
										{i + 1}
									</span>
									<span className="flex-1 text-foreground">{label}</span>
									<span className="font-medium text-foreground">
										{views.toLocaleString()}
									</span>
								</li>
							))}
						</ol>
					</div>

					{/* Bottom Menu */}
					<div>
						<p className="text-sm font-medium text-muted-foreground mb-3">
							Bottom Menu
						</p>
						<ol className="space-y-2">
							{MOCK_BOTTOM_MENU.map(({ label, views }, i) => (
								<li key={label} className="flex items-center gap-3 text-sm">
									<span className="w-5 text-zinc-400 text-right shrink-0">
										{i + 1}
									</span>
									<span className="flex-1 text-foreground">{label}</span>
									<span className="font-medium text-foreground">
										{views.toLocaleString()}
									</span>
								</li>
							))}
						</ol>
					</div>
				</div>
			</div>

			{/* Section 6: Adoption */}
			<div className="mb-8">
				<h2 className="font-heading text-lg font-medium text-foreground block mb-4">
					Adoption by Location
				</h2>
				<div className="rounded-xl border border-border bg-subtle shadow-sm p-6">
					{/* Group by selector */}
					<div
						className="inline-flex bg-white border border-border p-1.5 rounded-xl shadow-xs mb-5"
						role="tablist"
						aria-label="Group by"
						onKeyDown={(e) =>
							handleTabKeyDown(
								e,
								["Region", "Area", "Store"] as const,
								adoptionGroup,
								(v) => setAdoptionGroup(v as "Region" | "Area" | "Store")
							)
						}
					>
						{(["Region", "Area", "Store"] as const).map((g) => (
							<button
								key={g}
								id={`adoption-tab-${g.toLowerCase()}`}
								type="button"
								role="tab"
								aria-selected={adoptionGroup === g}
								aria-controls="adoption-tabpanel"
								tabIndex={adoptionGroup === g ? 0 : -1}
								onClick={() => setAdoptionGroup(g)}
								className={[
									"px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200",
									adoptionGroup === g
										? "bg-primary text-white shadow-md shadow-primary/50"
										: "text-zinc-500 hover:text-foreground hover:bg-white/80"
								].join(" ")}
							>
								{g}
							</button>
						))}
					</div>

					<div
						id="adoption-tabpanel"
						role="tabpanel"
						aria-labelledby={`adoption-tab-${adoptionGroup.toLowerCase()}`}
					>
						<table className="w-full text-sm">
							<thead>
								<tr className="border-b border-border">
									<th className="text-left py-2 pr-4 font-medium text-muted-foreground">
										{adoptionGroup}
									</th>
									<th className="text-right py-2 font-medium text-muted-foreground">
										Active Users
									</th>
								</tr>
							</thead>
							<tbody>
								{MOCK_ADOPTION_ROWS.map((row) => (
									<tr
										key={row.location}
										className="border-b border-border last:border-0"
									>
										<td className="py-3 pr-4 text-foreground">
											{row.location}
										</td>
										<td className="py-3 text-right text-foreground">
											{row.activeUsers.toLocaleString()}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</Page>
	);
};

export default Analytics;
